import React from "react";
import { Box, Separator, Text } from "@salestrak/ui";
import { marked, type Token, type Tokens } from "marked";

// Inline tokens (strong/em/link/…) nest inside their block's Text and
// inherit its font-size, so only block-level Text carries the size.
const BODY_SIZE = "sm";

// Heading size by depth (h1, h2, h3+), one step down from the page scale to
// match the compact body.
const HEADING_SIZES = ["lg", "base", "sm"] as const;

type Props = { markdown: string };

/**
 * Markdown renderer for What's-New announcement bodies (marked lexer →
 * library Text/Box), fixed at the compact scale of the feed cards; raw HTML
 * tokens are ignored by design. Reusing it elsewhere means inheriting that
 * scale — add a semantic density prop first if another surface needs
 * full-size markdown.
 */
export function MarkdownContent({ markdown }: Props) {
  const tokens = React.useMemo(() => marked.lexer(markdown), [markdown]);
  return (
    <Box direction="column" gap="sm">
      {tokens.map((token, i) => (
        <BlockToken key={i} token={token} />
      ))}
    </Box>
  );
}

function BlockToken({ token }: { token: Token }) {
  switch (token.type) {
    case "heading":
      return <Heading token={token as Tokens.Heading} />;
    case "paragraph":
      return (
        <Text size={BODY_SIZE}>
          <InlineTokens tokens={(token as Tokens.Paragraph).tokens ?? []} />
        </Text>
      );
    case "list":
      return <MarkdownList token={token as Tokens.List} />;
    case "code":
      return <CodeBlock token={token as Tokens.Code} />;
    case "blockquote":
      return <Blockquote token={token as Tokens.Blockquote} />;
    case "hr":
      return (
        <Box paddingY="sm">
          <Separator />
        </Box>
      );
    case "space":
      return null;
    case "html":
      // Raw HTML in changelogs is ignored — we render structured tokens only.
      return null;
    default:
      // Fallback: render the raw text if we don't recognize the block.
      return <Text size={BODY_SIZE}>{"raw" in token ? token.raw : ""}</Text>;
  }
}

function Heading({ token }: { token: Tokens.Heading }) {
  const size = HEADING_SIZES[Math.min(token.depth, 3) - 1];
  // Padding, not margin — Box has no margin props, and the surface is
  // transparent so the extra lead reads identically.
  return (
    <Box paddingTop="xs">
      <Text size={size} weight="semibold">
        <InlineTokens tokens={token.tokens ?? []} />
      </Text>
    </Box>
  );
}

function MarkdownList({ token }: { token: Tokens.List }) {
  const start = typeof token.start === "number" ? token.start : 1;
  return (
    <Box direction="column" gap="2xs" paddingLeft="sm">
      {token.items.map((item, i) => (
        <Box key={i} align="start" direction="row" gap="sm">
          <Text size={BODY_SIZE} variant="primary">
            {token.ordered ? `${start + i}.` : "•"}
          </Text>
          <Box flex={1}>
            <ListItemBody item={item} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ListItemBody({ item }: { item: Tokens.ListItem }) {
  // A list item's children are a mix of inline tokens and nested blocks
  // (paragraphs, sublists, code). Group runs of inline tokens into a single
  // Text and render block children with BlockToken.
  const children: React.ReactNode[] = [];
  let inlineRun: Token[] = [];
  const flushInline = (key: string) => {
    if (inlineRun.length === 0) return;
    children.push(
      <Text key={key} size={BODY_SIZE}>
        <InlineTokens tokens={inlineRun} />
      </Text>,
    );
    inlineRun = [];
  };

  (item.tokens ?? []).forEach((child, i) => {
    if (child.type === "text") {
      // marked nests inline tokens inside text tokens for list items.
      const t = child as Tokens.Text;
      if (t.tokens && t.tokens.length > 0) {
        inlineRun.push(...t.tokens);
      } else {
        inlineRun.push(t);
      }
      return;
    }
    if (isInlineToken(child)) {
      inlineRun.push(child);
      return;
    }
    flushInline(`inline-${i}`);
    children.push(<BlockToken key={`block-${i}`} token={child} />);
  });
  flushInline("inline-end");

  return (
    <Box direction="column" gap="2xs">
      {children}
    </Box>
  );
}

function isInlineToken(token: Token): boolean {
  return (
    token.type === "strong" ||
    token.type === "em" ||
    token.type === "codespan" ||
    token.type === "link" ||
    token.type === "br" ||
    token.type === "del" ||
    token.type === "escape"
  );
}

function CodeBlock({ token }: { token: Tokens.Code }) {
  return (
    <Box surface="sunken" padding="md" rounded="md">
      <Text as="code" mono size={BODY_SIZE}>
        {token.text}
      </Text>
    </Box>
  );
}

function Blockquote({ token }: { token: Tokens.Blockquote }) {
  return (
    <Box border="left" className="border-l-2" paddingLeft="md">
      <Box direction="column" gap="sm">
        {(token.tokens ?? []).map((t, i) => (
          <BlockToken key={i} token={t} />
        ))}
      </Box>
    </Box>
  );
}

function InlineTokens({ tokens }: { tokens: Token[] }) {
  return (
    <>
      {tokens.map((t, i) => (
        <InlineToken key={i} token={t} />
      ))}
    </>
  );
}

function InlineToken({ token }: { token: Token }) {
  switch (token.type) {
    case "text": {
      const t = token as Tokens.Text;
      if (t.tokens && t.tokens.length > 0) {
        return <InlineTokens tokens={t.tokens} />;
      }
      return <>{t.text}</>;
    }
    case "strong":
      return (
        <Text weight="semibold">
          <InlineTokens tokens={(token as Tokens.Strong).tokens ?? []} />
        </Text>
      );
    case "em":
      return (
        <Text italic>
          <InlineTokens tokens={(token as Tokens.Em).tokens ?? []} />
        </Text>
      );
    case "codespan":
      return (
        <Text as="code" mono>
          {(token as Tokens.Codespan).text}
        </Text>
      );
    case "del":
      return (
        <Text strike>
          <InlineTokens tokens={(token as Tokens.Del).tokens ?? []} />
        </Text>
      );
    case "link": {
      const link = token as Tokens.Link;
      return (
        <Text
          as="a"
          href={link.href}
          rel="noopener noreferrer"
          target="_blank"
          variant="link"
        >
          <InlineTokens tokens={link.tokens ?? []} />
        </Text>
      );
    }
    case "br":
      return <>{"\n"}</>;
    case "escape":
      return <>{(token as Tokens.Escape).text}</>;
    default:
      return <>{"raw" in token ? token.raw : ""}</>;
  }
}
